import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { logout, tokenReceived } from '../features/auth/authSlice';
import { refrescarSesion } from './refrescarSesion';
import { RootState } from '../redux/store';
import { settings } from '../settings';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { rgrlVinculationApi } from './rgrlVinculationApi';
import { PRESENTATION_TYPE } from '../pages/Presentaciones/consts';

export interface getPdfRrglResponse {
    data: {
        presignedUrls: string[];
    };
}

interface GetNominaDownloadUrlResponse {
    data: {
        presignedUrls: string[];
    };
}

interface PresentationType {
    id: number;
    nombre: string;
    titulo: string;
    detalle: string;
    activo: boolean;
    solapas: any;
}

interface CreatePresentationRequest {
    idTipoPresentacion: number;
    idEstablecimiento: number;
    esBorrador: boolean;
    datosAdicionales: {
        superficie: number;
        personalAdministracion: number;
        personalProduccion: number;
        actividad: string;
    };
    respuestas: string[];
    datosGremiales: string[];
    contratistas: string[];
    responsables: string[];
}

interface Cargo {
    id: number;
    codigo: string;
    nombre: string;
}

interface Representacion {
    id: number;
    nombre: string;
}

interface TipoContratacion {
    id: number;
    nombre: string;
}

interface ParametricDataResponse {
    data: {
        cargos: Cargo[];
        representaciones: Representacion[];
        tiposContratacion: TipoContratacion[];
    };
}

const baseQuery = fetchBaseQuery({
    baseUrl: `${settings.url.host}${settings.url.api}`,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        const accessToken = (getState() as RootState).auth.accessToken;
        if (accessToken) {
            headers.set('authorization', `Bearer ${accessToken}`);
        }
        return headers;
    }
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    let result: any = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        const refreshResult = await refrescarSesion(baseQuery, api, extraOptions);
        if (refreshResult?.data) {
            api.dispatch(tokenReceived(refreshResult.data));
            result = await baseQuery(args, api, extraOptions);
        } else {
            api.dispatch(logout());
        }
    }
    return result;
};

export const presentacionesApi = createApi({
    reducerPath: 'presentaciones',
    tagTypes: [
        'Presentaciones',
        'PresentacionById',
        'TiposPresentaciones',
        'VincluationRGRLPresentations'
    ],
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getTiposPresentaciones: builder.query<PresentationType[], void>({
            query: () => ({ url: 'presentaciones/tipos' }),
            providesTags: ['TiposPresentaciones'],
            transformResponse: (response: { data: any }) => response?.data?.tipos ?? []
        }),
        getTipoPresentacion: builder.query<PresentationType, number>({
            query: (idTipo) => ({ url: `presentaciones/tipos/${idTipo}` }),
            providesTags: ['TiposPresentaciones'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        createPresentacion: builder.mutation({
            query: (body: CreatePresentationRequest) => ({
                url: 'presentaciones',
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['Presentaciones']
        }),
        getPresentacionesByEstablecimiento: builder.query({
            query: ({ idEstablecimiento, take, skip, idTipo, ...params }) => ({
                url: `presentaciones`,
                params: { idEstablecimiento, take, skip, idTipo, ...params }
            }),
            providesTags: ['Presentaciones']
        }),
        getAllPresentaciones: builder.query({
            query: ({
                take,
                skip,
                idTipo,
                idEstablecimiento,
                idOrganismoCentral,
                organismo,
                idOrganismo,
                idOrganizacion,
                estado,
                fechaDesde,
                fechaHasta
            }) => ({
                url: `presentaciones/total`,
                params: {
                    take,
                    skip,
                    idTipo,
                    idEstablecimiento,
                    idOrganismoCentral,
                    organismo,
                    idOrganismo,
                    idOrganizacion,
                    estado,
                    fechaDesde,
                    fechaHasta
                }
            }),
            providesTags: ['Presentaciones'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getPresentacion: builder.query({
            query: (idPresentacion) => ({ url: `presentaciones/${idPresentacion}` }),
            providesTags: ['PresentacionById'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        editPresentacion: builder.mutation({
            query: ({ PresentacionId, body }) => ({
                url: `presentaciones/${PresentacionId}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['PresentacionById', 'Presentaciones'],
            onQueryStarted: (arg, api) => {
                api.queryFulfilled.then(() => {
                    api.dispatch(
                        rgrlVinculationApi.util.invalidateTags(['VincluationRGRLPresentations'])
                    );
                });
            }
        }),
        updatePresentationState: builder.mutation({
            query: ({
                presentationId,
                body
            }: {
                presentationId: number;
                body: { idEstado: any };
            }) => ({
                url: `presentaciones/${presentationId}/estados`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Presentaciones']
        }),
        updateMassivePresentationsStates: builder.mutation({
            query: (body: { idEstado: any; ids: number[] }) => ({
                url: `presentaciones/estados/masivo`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Presentaciones']
        }),
        exportMassivePresentations: builder.mutation({
            query: (body: { idsPresentaciones: number[] }) => ({
                url: `presentaciones/exportar/ids`,
                method: 'POST',
                body: body,
                responseHandler: async (response) => {
                    // Force it to be parsed as plain text instead of JSON
                    const text = await response.text();
                    return text;
                }
            })
        }),
        exportMassivePresentationsByIdsAsync: builder.mutation<string, { idsPresentaciones: number[] }>({
            query: (body) => ({
                url: `presentaciones/exportar/ids`,
                method: 'POST',
                body,
                responseHandler: async (response) => {
                    const text = await response.text();
                    return text;
                }
            })
        }),
        getMassivePresentationsByIdsFileStatus: builder.query({
            query: (jobId: string) => ({
                url: `presentaciones/exportar/async/${jobId}`
            })
        }),
        deletePresentation: builder.mutation({
            query: ({ idPresentation }) => ({
                url: `/presentaciones/seccion/${idPresentation}/`,
                method: 'DELETE'
            })
        }),
        getDatosParametricos: builder.query<ParametricDataResponse, void>({
            query: () => ({
                url: 'presentaciones/datos-parametricos'
            })
        }),
        getPesentationsIndicators: builder.query({
            query: ({ idOrganismoCentral, idOrganizacion }) => ({
                url: `presentaciones/indicadores`,
                params: { idOrganismoCentral, idOrganizacion }
            }),
            providesTags: ['Presentaciones'],
            transformResponse: (response: { data: any }) => response?.data,
            keepUnusedDataFor: 5
        }),
        getPresentacionRGRLPDFbyId: builder.query<
            getPdfRrglResponse,
            { idPresentacion: number; typeId: number; conforme?: boolean }
        >({
            query: ({ idPresentacion, typeId, conforme = false }) => ({
                url: `presentaciones/${idPresentacion}/archivo`,
                params: { idTipo: 2, conforme: conforme }
            })
        }),
        getNominaDownloadUrl: builder.query<
            GetNominaDownloadUrlResponse,
            { idPresentacion: number; idTipo: number }
        >({
            query: ({ idPresentacion, idTipo }) => ({
                url: `presentaciones/${idPresentacion}/archivo`,
                params: { idTipo: idTipo }
            })
        }),
        getCancerigenosPdf: builder.mutation<
            { data: { presignedUrls: string[] } },
            { idInforme: number }
        >({
            query: ({ idInforme }) => ({
                url: `cancerigenos/${idInforme}/pdf`,
                method: 'POST'
            })
        }),
        downloadPresentationsRAR: builder.mutation<string, FormData>({
            async queryFn(formData, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: '/presentaciones/exportar/async',
                    method: 'POST',
                    body: formData,
                    responseHandler: async (response) => {
                        // Force it to be parsed as plain text instead of JSON
                        const text = await response.text();
                        return text;
                    }
                });

                if (result.error) {
                    return { error: result.error as any };
                }

                return { data: result.data as string }; // Base64 string
            }
        }),
        getPresentationRarFileStatus: builder.query({
            query: (jobId: string) => ({
                url: `presentaciones/exportar/async/${jobId}`
            })
        }),
        getEstablishmentDetailsPresentations: builder.query({
            query: (idEstablecimiento) => ({
                url: `presentaciones`,
                params: {
                    presentacionesRRC: true,
                    idEstablecimiento: idEstablecimiento,
                    skip: 0,
                    take: 3
                }
            })
        }),
        //nuevos endpoints para las TABS DE PRESENTACIONES BO (antes se traían unificadas con getAllPresentaciones)
        getRGRLPresentacionesBO: builder.query({
            query: (params) => ({
                url: `presentaciones/total`,
                params: {
                    ...params,
                    excluirBajas: false,
                    ...(params.idTipo === undefined ? { allRGRLs: true } : {})
                }
            }),
            providesTags: ['Presentaciones'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getSVCCPresentacionesBO: builder.query({
            query: (params) => ({
                url: `presentaciones/total`,
                params: { ...params, idTipo: PRESENTATION_TYPE.CANCERIGENOS, excluirBajas: false }
            }),
            providesTags: ['Presentaciones'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getRARPresentacionesBO: builder.query({
            query: (params) => ({
                url: `presentaciones/total`,
                params: { ...params, idTipo: PRESENTATION_TYPE.RAR, excluirBajas: false }
            }),
            providesTags: ['Presentaciones'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        getEstablishmentPresentationPermissions: builder.query({
            query: ({ idEstablecimiento }: { idEstablecimiento: number }) => ({
                url: `presentaciones/establecimientos/${idEstablecimiento}/info`
            })
        }),
        exportRGRLPresentaciones: builder.mutation<string, Record<string, any>>({
            query: (body) => ({
                url: 'presentaciones/exportar/rgrl',
                method: 'POST',
                body,
                responseHandler: (response) => response.text()
            })
        })
    })
});

export const {
    useGetTiposPresentacionesQuery,
    useGetTipoPresentacionQuery,
    useCreatePresentacionMutation,
    useGetPresentacionesByEstablecimientoQuery,
    useLazyGetPresentacionesByEstablecimientoQuery,
    useGetPresentacionQuery,
    useEditPresentacionMutation,
    useUpdatePresentationStateMutation,
    useUpdateMassivePresentationsStatesMutation,
    useExportMassivePresentationsMutation,
    useExportMassivePresentationsByIdsAsyncMutation,
    useLazyGetMassivePresentationsByIdsFileStatusQuery,
    useGetDatosParametricosQuery,
    useGetPesentationsIndicatorsQuery,
    useLazyGetPresentacionRGRLPDFbyIdQuery,
    useLazyGetNominaDownloadUrlQuery,
    useGetAllPresentacionesQuery,
    useLazyGetAllPresentacionesQuery,
    useDeletePresentationMutation,
    useGetCancerigenosPdfMutation,
    useDownloadPresentationsRARMutation,
    useLazyGetPresentationRarFileStatusQuery,
    useGetEstablishmentDetailsPresentationsQuery,
    useLazyGetRGRLPresentacionesBOQuery,
    useLazyGetSVCCPresentacionesBOQuery,
    useLazyGetRARPresentacionesBOQuery,
    useLazyGetEstablishmentPresentationPermissionsQuery,
    useExportRGRLPresentacionesMutation
} = presentacionesApi;
